/**
 * StartupOrchestrator — runs init steps in a deterministic order,
 * isolating failures so one bad subsystem cannot block the rest.
 *
 * Each step is best-effort: errors are logged and counted, never thrown.
 */
export type StartupStep = {
  id: string;
  run: () => void | Promise<void>;
  required?: boolean;
};

export interface StartupReport {
  startedAt: number;
  finishedAt: number;
  ok: string[];
  failed: { id: string; error: string }[];
}

let lastReport: StartupReport | null = null;

export async function runStartup(steps: StartupStep[]): Promise<StartupReport> {
  const startedAt = Date.now();
  const ok: string[] = [];
  const failed: { id: string; error: string }[] = [];
  for (const step of steps) {
    try {
      await step.run();
      ok.push(step.id);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      failed.push({ id: step.id, error: msg });
      // eslint-disable-next-line no-console
      console.warn(`[startup] step "${step.id}" failed:`, msg);
    }
  }
  lastReport = { startedAt, finishedAt: Date.now(), ok, failed };
  return lastReport;
}

export function getStartupReport(): StartupReport | null {
  return lastReport;
}
